import axios from "axios";
import { APP_CONFIG } from "@/config/app.config";
import { apiLanguage } from "@/i18n";

// Создаем экземпляр axios
const axiosInstance = axios.create({
  baseURL: APP_CONFIG.api.baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: APP_CONFIG.api.timeout,
});

// Получаем CSRF токен перед первым запросом
let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string | null> | null = null;
/** Після спроби: API на іншому origin — XSRF у document.cookie недоступний (норма), повторно не дёргаємо sanctum. */
let csrfUnavailableCrossOrigin = false;

function readXsrfFromDocumentCookie(): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("XSRF-TOKEN="))
    ?.split("=")[1];
}

/** SPA і axios baseURL на різних origin — cookie Sanctum лишається на домені API, JS на сторінці її не бачить. */
export function isApiCrossOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const base = String(APP_CONFIG.api.baseURL || "").trim();
  if (!base.startsWith("http://") && !base.startsWith("https://")) {
    return false;
  }
  try {
    return new URL(base).origin !== window.location.origin;
  } catch {
    return false;
  }
}

// Константы для кэширования токена
const CSRF_TOKEN_CACHE_KEY = "csrf_token";
const CSRF_TOKEN_TIME_KEY = "csrf_token_time";
const CSRF_TOKEN_LIFETIME = 2 * 60 * 60 * 1000; // 2 часа в миллисекундах

/**
 * Проверяет кэшированный CSRF токен в sessionStorage
 * @returns {string | null} - Кэшированный токен если он валиден, иначе null
 */
function getCachedCsrfToken(): string | null {
  try {
    const cachedToken = sessionStorage.getItem(CSRF_TOKEN_CACHE_KEY);
    const tokenTime = sessionStorage.getItem(CSRF_TOKEN_TIME_KEY);

    if (!cachedToken || !tokenTime) {
      return null;
    }

    const tokenAge = Date.now() - parseInt(tokenTime, 10);

    // Токен валиден 2 часа
    if (tokenAge < CSRF_TOKEN_LIFETIME) {
      return cachedToken;
    }

    // Токен устарел - очищаем кэш
    sessionStorage.removeItem(CSRF_TOKEN_CACHE_KEY);
    sessionStorage.removeItem(CSRF_TOKEN_TIME_KEY);
    return null;
  } catch (error) {
    // Если sessionStorage недоступен - игнорируем кэширование
    return null;
  }
}

/**
 * Сохраняет CSRF токен в sessionStorage
 * @param {string} token - Токен для сохранения
 */
function cacheCsrfToken(token: string): void {
  try {
    sessionStorage.setItem(CSRF_TOKEN_CACHE_KEY, token);
    sessionStorage.setItem(CSRF_TOKEN_TIME_KEY, Date.now().toString());
  } catch (error) {
    // Если sessionStorage недоступен - игнорируем кэширование
    console.warn("Failed to cache CSRF token:", error);
  }
}

/**
 * Получает CSRF токен с защитой от race condition
 * Если несколько запросов происходят одновременно, они будут ждать один общий запрос
 * @returns {Promise<string | null>} - CSRF токен или null
 */
async function getCsrfToken(): Promise<string | null> {
  // Если токен уже есть в памяти - вернуть его
  if (csrfToken) {
    return csrfToken;
  }

  if (csrfUnavailableCrossOrigin) {
    return null;
  }

  // Проверяем кэш в sessionStorage
  const cachedToken = getCachedCsrfToken();
  if (cachedToken) {
    csrfToken = cachedToken;
    if (import.meta.env.DEV) {
      console.log("✅ CSRF token loaded from cache");
    }
    return csrfToken;
  }

  // Если запрос уже идёт - подождать его завершения (защита от race condition)
  if (csrfTokenPromise) {
    if (import.meta.env.DEV) {
      console.log("⏳ Waiting for existing CSRF token request...");
    }
    return csrfTokenPromise;
  }

  // Создаём новый запрос
  if (import.meta.env.DEV) {
    console.log("🔄 Fetching new CSRF token...");
  }
  csrfTokenPromise = (async () => {
    try {
      await axiosInstance.get("/sanctum/csrf-cookie");

      const raw = readXsrfFromDocumentCookie();

      if (raw) {
        csrfToken = decodeURIComponent(raw);
        cacheCsrfToken(csrfToken);
        if (import.meta.env.DEV) {
          console.log("✅ CSRF token fetched and cached");
        }
      } else if (isApiCrossOrigin()) {
        // Очікувано: Set-Cookie для іншого origin не потрапляє в document.cookie на сторінці SPA.
        csrfUnavailableCrossOrigin = true;
      } else if (import.meta.env.DEV) {
        console.warn("⚠️ CSRF token not found in cookies");
      }

      return csrfToken;
    } catch (error) {
      console.error("❌ Failed to get CSRF token:", error);
      return null;
    } finally {
      // Сбрасываем промис чтобы при следующей попытке мог быть создан новый запрос
      csrfTokenPromise = null;
    }
  })();

  return csrfTokenPromise;
}

export function isAnchorPlanApiUrl(url: string): boolean {
  return url.includes("/api/anchor-plan/") || url.includes("/anchor-plan/");
}

// Интерсептор для добавления токенов к запросам
axiosInstance.interceptors.request.use(async (config) => {
  const url = String(config.url || "");
  const authToken = localStorage.getItem("auth_token");
  const anchorPlan = isAnchorPlanApiUrl(url);
  // Same-origin: Anchor Plan — лише Sanctum cookie (протухлий Bearer блокує сесію).
  // Cross-origin prod (api.* vs SPA): cookie недоступна — потрібен Bearer з Google OAuth.
  if (authToken && (!anchorPlan || isApiCrossOrigin())) {
    config.headers["Authorization"] = `Bearer ${authToken}`;
  }

  // Мова інтерфейсу на момент запиту: бекенд перекладає свої повідомлення,
  // назви пресетів і пояснення методики (middleware SetLocaleFromAcceptLanguage).
  config.headers["Accept-Language"] = apiLanguage();

  // Для POST, PUT, DELETE запросов добавляем CSRF токен
  if (["post", "put", "delete"].includes(config.method?.toLowerCase() || "")) {
    const token = await getCsrfToken();
    if (token) {
      config.headers["X-XSRF-TOKEN"] = token;
    }
  }
  return config;
});

/**
 * Очищает кэшированный CSRF токен
 * Используется при logout или при ошибках авторизации
 */
export function clearCsrfToken(): void {
  csrfToken = null;
  csrfTokenPromise = null;
  csrfUnavailableCrossOrigin = false;
  try {
    sessionStorage.removeItem(CSRF_TOKEN_CACHE_KEY);
    sessionStorage.removeItem(CSRF_TOKEN_TIME_KEY);
    if (import.meta.env.DEV) {
      console.log("🧹 CSRF token cache cleared");
    }
  } catch (error) {
    // Игнорируем ошибки очистки кэша
  }
}

/** Протухлий Bearer блокує Sanctum cookie-сесію на auth:sanctum — повтор без Authorization. */
function isAnchorPlanProjectsIndexUrl(url: string): boolean {
  if (!url.includes("/projects")) {
    return false;
  }
  return !/\/projects\/[^/?#]+/.test(url);
}

/** Протухлий Bearer на GET /projects → 200 без user; повтор лише по cookie. */
async function retryAnchorPlanProjectsWithoutBearer(
  response: import("axios").AxiosResponse,
): Promise<import("axios").AxiosResponse> {
  const config = response.config;
  if (
    !config ||
    (config as { __anchorPlanGuestRetry?: boolean }).__anchorPlanGuestRetry
  ) {
    return response;
  }
  const url = String(config.url || "");
  if (!isAnchorPlanProjectsIndexUrl(url)) {
    return response;
  }
  const body = response.data as { user?: unknown } | undefined;
  if (body?.user != null || !localStorage.getItem("auth_token")) {
    return response;
  }
  localStorage.removeItem("auth_token");
  (config as { __anchorPlanGuestRetry?: boolean }).__anchorPlanGuestRetry = true;
  stripAuthorizationHeader(config.headers as Record<string, unknown> | undefined);
  return axiosInstance.request(config);
}

/** Протухлий Bearer блокує Sanctum cookie-сесію на auth:sanctum — повтор без Authorization. */
function stripAuthorizationHeader(
  headers: Record<string, unknown> | undefined,
): void {
  if (!headers) return;
  const h = headers as Record<string, unknown> & {
    delete?: (name: string) => void;
  };
  if (typeof h.delete === "function") {
    h.delete("Authorization");
    h.delete("authorization");
    return;
  }
  delete h.Authorization;
  delete h.authorization;
}

// Response interceptor for better error handling
axiosInstance.interceptors.response.use(
  async (response) => retryAnchorPlanProjectsWithoutBearer(response),
  async (error) => {
    const config = error.config;
    if (
      error.response?.status === 401 &&
      config &&
      !(config as { __anchorPlanBearerRetry?: boolean }).__anchorPlanBearerRetry
    ) {
      const url = String(config.url || "");
      const hadBearer =
        !!localStorage.getItem("auth_token") ||
        !!(config.headers &&
          (config.headers as Record<string, string>).Authorization);
      if (isAnchorPlanApiUrl(url) && hadBearer) {
        localStorage.removeItem("auth_token");
        (config as { __anchorPlanBearerRetry?: boolean }).__anchorPlanBearerRetry =
          true;
        stripAuthorizationHeader(
          config.headers as Record<string, unknown> | undefined,
        );
        return axiosInstance.request(config);
      }
    }

    if (error.response?.status === 401) {
      // Проверяем, не является ли это запросом к защищенным endpoints
      // Для этих endpoints НЕ удаляем токен при 401 (может быть проблема с правами доступа,
      // но пользователь авторизован)
      const url = error.config?.url || "";
      const isProtectedRequest =
        url.includes("/api/statistics/") ||
        url.includes("/api/maintenance/") ||
        url.includes("/api/knowledge-base/") ||
        url.includes("/api/session/") ||
        url.includes("/api/anchor-plan/");

      if (!isProtectedRequest) {
        // Для всех остальных запросов при 401 удаляем токен
        localStorage.removeItem("auth_token");
        // Очищаем CSRF токен при 401
        clearCsrfToken();
        // Не редиректим автоматически, пусть приложение само решит что делать
      }
    } else if (error.response?.status === 419) {
      // 419 = CSRF token mismatch - сбрасываем кэш и позволяем повторить запрос
      console.warn("⚠️ CSRF token mismatch (419) - clearing cache");
      clearCsrfToken();
    } else if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
      console.error("Network error or timeout:", error);
      console.error("Request URL:", error.config?.url);
      console.error("Request method:", error.config?.method);
      console.error(
        "Request data size:",
        error.config?.data ? JSON.stringify(error.config.data).length : 0,
      );
    } else if (error.response?.status === 413) {
      console.error("Request payload too large");
    } else if (error.response?.status === 0 || !error.response) {
      console.error("CORS error or network failure:", error);
      console.error(
        "This might be due to request size or server configuration",
      );
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
